/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { personCsv } from '../../helpers/admin-enrolment.helper';
import { AdminEnrolment, AdminEnrolmentPerson, AdminEnrolmentRequest } from '../../models/playlist/admin-enrolment.model';
import { ObservableResult } from '../../store';
import { RestClientService } from '../rest-client.service';
import { AdminEnrolmentsDataService } from './admin-enrolments-data.service';

@Injectable()
export class ApiAdminEnrolmentsDataService implements AdminEnrolmentsDataService {
  constructor(private client: RestClientService) {}

  enrol(playlistUid: string, request: AdminEnrolmentRequest): ObservableResult<AdminEnrolment> {
    return this.client.post<AdminEnrolment>(this.url(playlistUid), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err: HttpErrorResponse) => ObservableResult.ofError(apiMessage(err))),
    );
  }

  importCsv(playlistUid: string, file: File, options: { preview: boolean; notify: boolean }): ObservableResult<AdminEnrolment> {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post<AdminEnrolment>(this.url(playlistUid, 'import'), formData, options).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err: HttpErrorResponse) => ObservableResult.ofError(apiMessage(err))),
    );
  }

  // Only the import previews, so one person is previewed as a file of one row.
  previewPerson(playlistUid: string, person: AdminEnrolmentPerson): ObservableResult<AdminEnrolment> {
    const file = new File([personCsv(person)], 'person.csv', { type: 'text/csv' });
    return this.importCsv(playlistUid, file, { preview: true, notify: false });
  }

  private url(playlistUid: string, path?: string): string {
    const base = `playlists/${playlistUid}/enrolments`;
    return Location.joinWithSlash(environment.apiRootUrl || '', path ? `${base}/${path}` : base);
  }
}

function apiMessage(err: HttpErrorResponse): string {
  return err?.status === 400 && err.error?.message ? err.error.message : '';
}
